import React, { useState } from "react";
import { RiskLevelEnum } from "../..";
import { translate } from "../../intl";
import { GenericModal } from "../../ui";
import { getRiskByColor } from "./UtilsRiskMatrixModal";
import riskMatrix from "./riskMatrix.json";
import { Captions, Content, Table } from "./styled";

interface CellProps {
  content?: string;
  colSpan?: number;
  rowSpan?: number;
  isHead?: boolean;
  className?: string;
}

const renderCell = (
  position: number[],
  activePosition: number[],
  setActivePosition: React.Dispatch<React.SetStateAction<number[]>>,
  setRiskLevelAndClose: (value: RiskLevelEnum | "") => void,
  cell: CellProps
) => {
  const Cell = cell.isHead ? "th" : "td";
  const isColor =
    cell.className && ["green", "orange", "red"].includes(cell.className);
  let { className } = cell;

  if (position[0] === activePosition[0] && position[1] < activePosition[1]) {
    className += " active-row";
  } else if (
    position[1] === activePosition[1] &&
    position[0] < activePosition[0]
  ) {
    className += " active-column";
  }
  if (position[0] === activePosition[0] && position[1] === activePosition[1]) {
    className += " active-cell";
  }

  return (
    <Cell
      key={position[0] + position[1]}
      data-testid={`${position[0] + position[1]}-${className}`}
      colSpan={cell.colSpan}
      rowSpan={cell.rowSpan}
      className={className}
      onMouseEnter={isColor ? () => setActivePosition(position) : undefined}
      onMouseLeave={() => setActivePosition([-1, -1])}
      onClick={
        isColor
          ? () => setRiskLevelAndClose(getRiskByColor(cell.className))
          : undefined
      }
    >
      {cell.content}
    </Cell>
  );
};

interface RiskMatrixModalProps {
  isOpen: boolean;
  onClose: () => void;
  setRiskLevel: (value: RiskLevelEnum | "") => void;
  readOnly: boolean;
}

export const RiskMatrixModal: React.FC<RiskMatrixModalProps> = ({
  isOpen,
  onClose,
  setRiskLevel,
  readOnly,
}: RiskMatrixModalProps) => {
  const [activePosition, setActivePosition] = useState([-1, -1]);
  const setRiskLevelAndClose = (value: RiskLevelEnum | "") => {
    setRiskLevel(value);
    onClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={() => onClose()}
      width="900px"
      header={<h2>{translate("ctoPage.riskMatrix.matrix")}</h2>}
      content={
        <Content>
          <Table readOnly={readOnly}>
            <tbody>
              {riskMatrix.map((row: CellProps[], rowIndex) => (
                <tr key={Math.random()}>
                  {row.map((cell, columnIndex) =>
                    renderCell(
                      [rowIndex, columnIndex],
                      activePosition,
                      setActivePosition,
                      setRiskLevelAndClose,
                      cell
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          <Captions>
            <div>
              <div className="circle green" />
              <div>
                <strong className="green">{translate("status.low")}</strong>{" "}
                {translate("ctoPage.riskMatrix.manage")}
              </div>
            </div>
            <div>
              <div className="circle orange" />
              <div>
                <strong className="orange">{translate("status.medium")}</strong>{" "}
                {translate("ctoPage.riskMatrix.identify")}
              </div>
            </div>
            <div>
              <div className="circle red" />
              <div>
                <strong className="red">{translate("status.high")}</strong>
                {translate("ctoPage.riskMatrix.shall")}
              </div>
            </div>
          </Captions>
        </Content>
      }
    />
  );
};
